/**
 * Receive Purchase Modal
 *
 * Modal to receive a single purchase line item, generating an inventory
 * batch via the receive_purchase_item RPC.
 *
 * @module components/purchases/ReceivePurchaseModal
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Form, Button, Spinner, Alert, Row, Col } from 'react-bootstrap';
import { receivePurchaseItem } from '../../services/purchaseService';
import { toast } from '../../utils/toast';
import { formatCurrency } from '../../utils/formatters';

const ReceivePurchaseModal = ({ show, onHide, item, onReceived, currency = 'CRC' }) => {
  const remaining = useMemo(() => {
    if (!item) return 0;
    const ordered = Number(item.quantity_ordered || 0);
    const received = Number(item.quantity_received || 0);
    return Math.max(0, ordered - received);
  }, [item]);

  const [quantity, setQuantity] = useState(remaining);
  const [lotNumber, setLotNumber] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Reset whenever the modal opens or the target item changes
  useEffect(() => {
    if (show && item) {
      setQuantity(remaining);
      setLotNumber(item.lot_number || '');
      setExpirationDate(item.expiration_date || '');
      setNotes('');
      setError(null);
    }
  }, [show, item, remaining]);

  const validate = () => {
    const q = Number(quantity);
    if (!q || q <= 0) {
      setError('La cantidad debe ser mayor a 0');
      return false;
    }
    if (q > remaining) {
      setError(`La cantidad no puede exceder ${remaining} (pendiente)`);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!validate()) return;

    setSubmitting(true);
    try {
      const { data, error: rpcError } = await receivePurchaseItem(
        item.id,
        Number(quantity),
        {
          lot_number: lotNumber || null,
          expiration_date: expirationDate || null,
          notes: notes || null,
        }
      );

      if (rpcError) {
        setError(rpcError.message || 'Error al recibir item');
        toast.error('Error al recibir item');
        return;
      }

      toast.success('Item recibido y lote generado');
      onReceived && onReceived(data);
      onHide && onHide();
    } catch (err) {
      console.error('Receive error:', err);
      setError(err.message || 'Error inesperado');
      toast.error('Error al recibir item');
    } finally {
      setSubmitting(false);
    }
  };

  if (!item) return null;

  const productName = item.inventory?.name || 'Producto';
  const sku = item.inventory?.sku;
  const unit = item.inventory?.unit;

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static">
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-box-arrow-in-down me-2"></i>
            Recibir item
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <div className="fw-semibold">
              {sku ? `${sku} - ` : ''}
              {productName}
            </div>
            <small className="text-muted">
              Pendiente: <strong>{remaining}</strong>
              {unit ? ` ${unit}` : ''}
              {' | '}
              Costo unitario: <strong>{formatCurrency(item.unit_cost, currency)}</strong>
            </small>
          </div>

          {error && (
            <Alert variant="danger" className="py-2">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {error}
            </Alert>
          )}

          <Row className="g-3">
            <Col xs={12}>
              <Form.Label>
                Cantidad a recibir <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="number"
                min="0"
                step="0.01"
                max={remaining}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                disabled={submitting}
                autoFocus
                required
              />
              <Form.Text className="text-muted">
                Maximo permitido: {remaining}
              </Form.Text>
            </Col>
            <Col xs={12} sm={6}>
              <Form.Label>Numero de lote</Form.Label>
              <Form.Control
                type="text"
                value={lotNumber}
                onChange={(e) => setLotNumber(e.target.value)}
                disabled={submitting}
                placeholder="Opcional"
                maxLength={50}
              />
            </Col>
            <Col xs={12} sm={6}>
              <Form.Label>Fecha vencimiento</Form.Label>
              <Form.Control
                type="date"
                value={expirationDate}
                onChange={(e) => setExpirationDate(e.target.value)}
                disabled={submitting}
              />
            </Col>
            <Col xs={12}>
              <Form.Label>Notas</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={submitting}
                placeholder="Observaciones de la recepcion (opcional)"
                maxLength={500}
              />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={submitting}>
            Cancelar
          </Button>
          <Button type="submit" variant="success" disabled={submitting}>
            {submitting ? (
              <>
                <Spinner size="sm" className="me-2" />
                Recibiendo...
              </>
            ) : (
              <>
                <i className="bi bi-check-circle me-2"></i>
                Confirmar recepcion
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ReceivePurchaseModal;
